/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import { Button } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useTranslation } from "@/i18n/client";
import to from "await-to-js";
import { giftQuery } from "@/common-components-src/api/gift/gift.api";
import { NewGiftCouponDto } from "@/common-components-src/api/gift/gift.interface";
import CloseIcon from "@/assets/icons/CloseIcon.svg";
import CouponFree from "../icon/couponFree.svg";
import CouponOther from "../icon/couponOther.svg";
import UKToast from "@/components/UKToast/index";
import JDILogin from "@/app/login/index.login";
import callAfterLogin from "@/app/login/utils/callAfterLogin";
import TruncatedName from "./truncated";
import { EXPOSURE_RECORD, sendClickTrack } from "@/tracks/25579";
import styles from "./coupon.module.scss";

interface DataItemProps {
  isOpenModal?: boolean; //打开弹窗
  setOpenModal?: (isOpen: boolean) => void; //设置弹窗打开状态
  closeModalCallback?: () => void; //弹窗关闭的回调方法
}

const GiftCouponModal: React.FC<DataItemProps> = ({ closeModalCallback, isOpenModal, setOpenModal }) => {
  const { t } = useTranslation();
  const [couponList, setCouponList] = useState<NewGiftCouponDto[] | null>(null); // 普通券
  const [shippingCoupons, setShippingCoupons] = useState<NewGiftCouponDto[] | null>(null); // 运费券
  const [isLogin, setIsLogin] = useState(false);
  const [activityTitle, setActivityTitle] = useState(""); //弹窗 title
  const [currencySymbol, setCurrencySymbol] = useState(""); //货币符号
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (!isOpenModal) {
      return;
    }
    loginStatus();
  }, [isOpenModal]);

  const loginStatus = async () => {
    const { isLoggedIn } = await JDILogin.getInstance().getUserInfo();
    setIsLogin(isLoggedIn);
    getGift();
  };

  const getGift = async () => {
    const [err, result] = await to(giftQuery({ scene: "QUERY" }));
    setPageLoading(false);
    if (err) {
      setCouponList(null);
      setShippingCoupons(null);
    } else {
      console.log("result---------GiftModal", result);
      if (result?.data?.activityTitle) {
        setActivityTitle(result?.data?.activityTitle);
      }
      setCurrencySymbol(result?.data?.currencySymbol);
      const list = result?.data?.couponList;
      if (list && list.length > 0) {
        setCouponList(result?.data?.productCoupons);
        setShippingCoupons(result?.data?.shippingCoupons);
      }
    }
    if (result?.code !== "200") {
      UKToast.show({
        content: `${t("gift.error")}`,
        icon: "error",
      });
    }
  };

  const handleClose = () => {
    setOpenModal && setOpenModal(false);
    closeModalCallback && closeModalCallback();
  };

  const gotoLogin = () => {
    handleClose();
    callAfterLogin(async () => {
      // const [err, result] = await to(giftQuery());
      // console.log("result---------GiftModal", result);
    });
  };

  const track = (list: any) => {
    const coupons: any[] = [];
    if (list && list.length > 0) {
      list.forEach((item: any) => {
        coupons.push({
          couponId: item?.couponId,
          batchId: item?.batchId || "-100",
          couponTag: "0", // 0 新人礼包
          use_threshold: item?.quota,
          couponType: item?.couponType === 1 ? "1" : "2",
          couponSave: item?.discount,
        });
      });
    }
    return {
      coupon: coupons,
      is_login: isLogin ? ("1" as const) : ("0" as const),
    };
  };

  const couponView = () => {
    return (
      <>
        <div className={styles.headerBox1}>
          <CouponOther /> <span className={styles.headeTitle1}>{t("gift.coupons")}</span>
        </div>
        {couponList?.map((coupon, index) => (
          <div
            className={styles.voucher}
            style={{
              border: `0.5px solid #ff9fa7`,
              borderLeft: `4px solid #ff9fa7`,
              minHeight: `96px`,
            }}
            key={index}
          >
            <div className={styles.nominal}>
              <span style={{ color: "#CC0C1C" }}>
                {currencySymbol}
                {coupon.discount}
              </span>
              <span className={styles.leftOff} style={{ color: "#CC0C1C" }}>
                {t("gift.off")}
              </span>
              <span className={styles.order}>{t("gift.over", { price: `${currencySymbol}${coupon.quota}` })}</span>
            </div>
            <div className={styles.dayFlex}>
              <span className={styles.day}>{t("gift.until", { time: coupon.endTime })}</span>
            </div>
            <>
              <div className={styles.line}></div>
              <TruncatedName text={coupon?.limitStr} index={index} track={{ postcode: "-100", clickPos: "1", ...track(couponList) }} />
            </>
          </div>
        ))}
      </>
    );
  };

  const couponPromptText = () => {
    const text = t("gift.orders", { num: shippingCoupons?.length || 0 });
    const freeDeliveryText = t("gift.freeBold"); // 获取动态文本
    return text.replace(
      new RegExp(freeDeliveryText, "g"),
      `<span style="font-weight: 900; color: #1A1A1A; font-family: SF Pro Display;">${freeDeliveryText}</span>`,
    );
  };

  const shippingCouponView = () => {
    return (
      <>
        <div className={styles.headerBox2}>
          <CouponFree /> <span className={styles.headeTitle2} dangerouslySetInnerHTML={{ __html: couponPromptText() }} />
        </div>
        {shippingCoupons?.map((coupon, index) => (
          <div
            className={styles.voucher}
            style={{
              border: `0.5px solid #ACE4B4`,
              borderLeft: `4px solid #ACE4B4`,
              minHeight: `77px`,
            }}
            key={index}
          >
            <div className={styles.nominal}>
              <span style={{ color: "#269636" }}>
                {currencySymbol}
                {coupon.discount}
              </span>
              <span className={styles.leftOff} style={{ color: "#269636" }}>
                {t("gift.shippingOff")}
              </span>
            </div>
            <div className={styles.dayFlex}>
              <span className={styles.day}>{t("gift.until", { time: coupon.endTime })}</span>
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <>
      {isOpenModal && !pageLoading && (
        <div className={styles.container}>
          <div
            className={styles.box}
            exptag={"exp|" + EXPOSURE_RECORD.NewCustomerGift_GiftLayerExpo}
            data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
            data-exptag-json-param={JSON.stringify(track([...(couponList || []), ...(shippingCoupons || [])]))}
          >
            <span className={styles.title}>{activityTitle}</span>
            {/* 优惠券数量 */}
            <div className={styles.boxVouchers}>
              {/* 普通券 */}
              {couponList && couponList.length > 0 && couponView()}
              {/* 运费券 */}
              {shippingCoupons && shippingCoupons.length > 0 && shippingCouponView()}
            </div>
            <div className={styles.bottomFloat}>
              <Button
                className={styles.buttonCheck}
                onClick={() => {
                  isLogin ? handleClose() : gotoLogin();
                  sendClickTrack.NewCustomerGift_GiftLayer({ postcode: "-100", clickPos: "2", ...track([...(couponList || []), ...(shippingCoupons || [])]) });
                }}
              >
                {isLogin ? `${t("gift.got")}` : `${t("gift.claim")}`}
              </Button>
              {!isLogin && <div className={styles.tips}>{t("gift.login")}</div>}
            </div>
            <CloseIcon
              className={styles.close}
              onClick={() => {
                handleClose();
                sendClickTrack.NewCustomerGift_GiftLayer({ postcode: "-100", clickPos: "0", ...track([...(couponList || []), ...(shippingCoupons || [])]) });
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default GiftCouponModal;
